import React, { useState } from "react";
import { useTable, List, EditButton, ShowButton, DeleteButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag, Button, Input, Select, Popconfirm, message } from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import { useCustomMutation } from "@refinedev/core";
import { PAYMENT_REQUEST_QUERY } from "./queries";
import { PaymentRequest, PaymentRequestStatus, PaymentRequestPriority } from "./interface";
import {
  SUBMIT_PAYMENT_REQUEST_MUTATION,
  COMPLETE_PAYMENT_REQUEST_PAYMENT_MUTATION,
  CANCEL_PAYMENT_REQUEST_MUTATION,
  FAIL_PAYMENT_REQUEST_MUTATION,
} from "./queries";

interface IPaymentRequestRow extends PaymentRequest {}

const StatusBadge: React.FC<{ status: PaymentRequestStatus }> = ({ status }) => {
  const colorMap: Record<PaymentRequestStatus, string> = {
    [PaymentRequestStatus.DRAFT]: "default",
    [PaymentRequestStatus.SUBMITTED]: "blue",
    [PaymentRequestStatus.COMPLETED]: "green",
    [PaymentRequestStatus.FAILED]: "red",
    [PaymentRequestStatus.CANCELLED]: "orange",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

const PriorityBadge: React.FC<{ priority: PaymentRequestPriority }> = ({ priority }) => {
  const colorMap: Record<PaymentRequestPriority, string> = {
    [PaymentRequestPriority.LOW]: "green",
    [PaymentRequestPriority.MEDIUM]: "blue",
    [PaymentRequestPriority.HIGH]: "orange",
    [PaymentRequestPriority.URGENT]: "red",
  };
  return <Tag color={colorMap[priority] || "default"}>{priority}</Tag>;
};

export const PaymentRequestList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<PaymentRequestStatus | undefined>(undefined);
  const [priorityFilter, setPriorityFilter] = useState<PaymentRequestPriority | undefined>(undefined);
  const [supplierFilter, setSupplierFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<IPaymentRequestRow>({
    resource: "paymentRequests",
    initialSorter: [{ field: "createdAt", order: "desc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: PAYMENT_REQUEST_QUERY,
    },
    onSearch: (params: any) => {
      const { q } = params;
      return [
        {
          field: "invoiceId",
          operator: "contains",
          value: q ? q : undefined,
        },
        {
          field: "poNo",
          operator: "contains",
          value: q ? q : undefined,
        },
        {
          field: "supplierName",
          operator: "contains",
          value: q ? q : undefined,
        },
      ];
    },
  });

  const { mutate: submitMutate, isLoading: submitting } = useCustomMutation();
  const { mutate: completeMutate, isLoading: completing } = useCustomMutation();
  const { mutate: cancelMutate, isLoading: cancelling } = useCustomMutation();
  const { mutate: failMutate, isLoading: failing } = useCustomMutation();

  const handleSubmit = (record: IPaymentRequestRow) => {
    submitMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestId: record.id,
          },
        },
        meta: { gqlMutation: SUBMIT_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request submitted successfully");
          window.location.reload();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to submit payment request");
        },
      },
    );
  };

  const handleComplete = (record: IPaymentRequestRow) => {
    completeMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestId: record.id,
            payment: {
              voucherNumber: `VCH-${Date.now()}`,
              paidAmount: record.amount,
              paymentDate: new Date().toISOString(),
              method: "Bank Transfer",
              reference: `REF-${Date.now()}`,
            },
          },
        },
        meta: { gqlMutation: COMPLETE_PAYMENT_REQUEST_PAYMENT_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment completed successfully");
          window.location.reload();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to complete payment");
        },
      },
    );
  };

  const handleCancel = (record: IPaymentRequestRow) => {
    cancelMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestId: record.id,
          },
        },
        meta: { gqlMutation: CANCEL_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request cancelled successfully");
          window.location.reload();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to cancel payment request");
        },
      },
    );
  };

  const handleFail = (record: IPaymentRequestRow) => {
    failMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            paymentRequestId: record.id,
          },
        },
        meta: { gqlMutation: FAIL_PAYMENT_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Payment request marked as failed");
          window.location.reload();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to mark payment request as failed");
        },
      },
    );
  };

  const dataSource: IPaymentRequestRow[] = ((tableProps as any)?.dataSource || []).filter(
    (row: IPaymentRequestRow) => {
      const matchesSearch =
        !searchTerm ||
        row.invoiceId?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
        row.poNo?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
        row.supplierName?.toLowerCase?.().includes(searchTerm.toLowerCase());

      const matchesStatus = !statusFilter || row.status === statusFilter;
      const matchesPriority = !priorityFilter || row.priority === priorityFilter;
      const matchesSupplier = !supplierFilter || row.supplierName === supplierFilter;

      return matchesSearch && matchesStatus && matchesPriority && matchesSupplier;
    }
  );

  // Get unique suppliers for filter
  const uniqueSuppliers = Array.from(
    new Set(((tableProps as any)?.dataSource || []).map((r: IPaymentRequestRow) => r.supplierName).filter(Boolean))
  ) as string[];

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Input
              placeholder="Search by Invoice ID, PO No, or Supplier..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 280 }}
            />
            <Select
              placeholder="Filter by Status"
              allowClear
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              {Object.values(PaymentRequestStatus).map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by Priority"
              allowClear
              style={{ width: 150 }}
              value={priorityFilter}
              onChange={setPriorityFilter}
            >
              {Object.values(PaymentRequestPriority).map((priority) => (
                <Select.Option key={priority} value={priority}>
                  {priority}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by Supplier"
              allowClear
              showSearch
              style={{ width: 200 }}
              value={supplierFilter}
              onChange={setSupplierFilter}
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {uniqueSuppliers.map((supplier) => (
                <Select.Option key={supplier} value={supplier} label={supplier}>
                  {supplier}
                </Select.Option>
              ))}
            </Select>
            <CreateButton />
          </Space>
        ),
      }}
    >
      <Table {...tableProps} dataSource={dataSource} rowKey="id">
        <Table.Column
          dataIndex="invoiceId"
          title="Invoice ID"
          sorter
          render={(invoiceId: string) => invoiceId || "-"}
        />
        <Table.Column
          dataIndex="poNo"
          title="PO No"
          sorter
          render={(poNo: string) => poNo || "-"}
        />
        <Table.Column
          dataIndex="supplierName"
          title="Supplier"
          sorter
          render={(supplierName: string) => supplierName || "-"}
        />
        <Table.Column
          dataIndex="amount"
          title="Amount"
          sorter
          render={(v: number, record: IPaymentRequestRow) =>
            `${record.currency || "ETB"} ${Number(v ?? 0).toLocaleString()}`
          }
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: PaymentRequestStatus) => <StatusBadge status={status} />}
          sorter
        />
        <Table.Column
          dataIndex="priority"
          title="Priority"
          render={(priority: PaymentRequestPriority) => <PriorityBadge priority={priority} />}
          sorter
        />
        <Table.Column
          dataIndex="dueDate"
          title="Due Date"
          sorter
          render={(date: string) => (date ? dayjs(date).format("YYYY-MM-DD") : "-")}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: IPaymentRequestRow) => (
            <Space size="small">
              <ShowButton size="small" recordItemId={record.id} resource="paymentRequests" />
              {record.status === PaymentRequestStatus.DRAFT && (
                <>
                  <EditButton size="small" recordItemId={record.id} resource="paymentRequests" />
                  <Popconfirm
                    title="Are you sure you want to delete this payment request?"
                    onConfirm={() => {
                      // Delete functionality will be handled by DeleteButton
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteButton size="small" recordItemId={record.id} resource="paymentRequests" />
                  </Popconfirm>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleSubmit(record)}
                    loading={submitting}
                  >
                    Submit
                  </Button>
                </>
              )}
              {record.status === PaymentRequestStatus.SUBMITTED && (
                <>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleComplete(record)}
                    loading={completing}
                  >
                    Complete
                  </Button>
                  <Popconfirm
                    title="Are you sure you want to cancel this payment request?"
                    onConfirm={() => handleCancel(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button size="small" danger loading={cancelling}>
                      Cancel
                    </Button>
                  </Popconfirm>
                </>
              )}
              {record.status === PaymentRequestStatus.FAILED && (
                <Popconfirm
                  title="Are you sure you want to cancel this payment request?"
                  onConfirm={() => handleCancel(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" danger loading={cancelling}>
                    Cancel
                  </Button>
                </Popconfirm>
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
